import React, { useState, useEffect } from "react";
import axios from "axios";
import { errMessage } from "../lib/errors";
import { Calendar as CalendarIcon, Users, Clock, MapPin, Info } from "lucide-react";
import { format } from "date-fns";
import { Calendar as DatePicker } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { restaurantInfo } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const times = [
  "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM",
  "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM", "9:30 PM"
];

export default function Reservation() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [dateOpen, setDateOpen] = useState(false);
  const [time, setTime] = useState("7:00 PM");
  const [party, setParty] = useState(2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { user, authHeaders } = useAuth();

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
      setPhone((p) => p || user.phone || "");
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast({ title: "Almost there", description: "Please add your name and email to confirm." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reservations`,
        { date, time, party_size: party, name, email, phone: phone.trim() || user?.phone || undefined },
        { headers: authHeaders }
      );
      toast({
        title: "Table reserved",
        description: `${name}, we'll see you on ${format(new Date(`${date}T00:00:00`), "EEE, d MMM yyyy")} at ${time} for ${party} guest${party > 1 ? "s" : ""}.`
      });
      if (!user) { setName(""); setEmail(""); setPhone(""); }
    } catch (err) {
      toast({
        title: "Booking failed",
        description: errMessage(err, "Please try again or call us.")
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reservation" className="py-24 md:py-36 bg-[var(--mm-bg-alt)] border-t border-b border-[var(--mm-line)]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div className="fade-in">
          <div className="section-label mb-6">Reservations</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] italic font-light text-[var(--mm-cream)] mb-6">
            Pull up a<br />
            <span className="text-[var(--mm-accent)]">seat at our table.</span>
          </h2>
          <p className="text-[var(--mm-cream-muted)] max-w-md font-light leading-relaxed">
            Whether it's a Tuesday dinner for two or a Sunday feast for the whole crew, we'd love to feed you. Reservations recommended, walk-ins welcome at the bar.
          </p>
          <div className="mt-10 flex items-center gap-6">
            <div className="h-px w-16 bg-[var(--mm-accent)]" />
            <span className="text-xs tracking-[0.28em] uppercase text-[var(--mm-cream-muted)]">Parties of 8+ email us</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="fade-in bg-[var(--mm-bg)] p-8 md:p-10 border border-[var(--mm-line)]">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">
                <CalendarIcon size={14} /> Date
              </span>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="reservation-date-trigger"
                    className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 text-left focus:border-[var(--mm-accent)] outline-none hover:border-[var(--mm-accent)] transition-colors"
                  >
                    {format(new Date(`${date}T00:00:00`), "EEE, d MMM yyyy")}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="reservation-date-calendar">
                  <DatePicker
                    mode="single"
                    weekStartsOn={1}
                    selected={new Date(`${date}T00:00:00`)}
                    onSelect={(d) => {
                      if (d) setDate(format(d, "yyyy-MM-dd"));
                      setDateOpen(false);
                    }}
                    disabled={{ before: new Date() }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </label>
            <label className="block">
              <span className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">
                <Clock size={14} /> Time
              </span>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              >
                {times.map((t) => <option key={t} className="bg-[var(--mm-bg)]">{t}</option>)}
              </select>
            </label>
          </div>

          <label className="block mb-4">
            <span className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">
              <Users size={14} /> Party Size
            </span>
            <select
              value={party}
              onChange={(e) => setParty(parseInt(e.target.value))}
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
            >
              {[1,2,3,4,5,6,7,8].map((n) => (
                <option key={n} className="bg-[var(--mm-bg)]" value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
              ))}
            </select>
          </label>

          <label className="block mb-4">
            <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
            />
          </label>

          <label className="block mb-4">
            <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
            />
          </label>

          <label className="block mb-6">
            <span className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2 block">Phone <span className="normal-case tracking-normal opacity-70">(so we can text to confirm)</span></span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="04xx xxx xxx"
              data-testid="reservation-phone-input"
              className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
            />
          </label>

          <button type="submit" disabled={submitting} className="btn-primary w-full text-center disabled:opacity-60">
            {submitting ? "Booking..." : "Find a Table"}
          </button>

          <div className="mt-8 pt-6 border-t border-[var(--mm-line)]" data-testid="reservation-info-section">
            <div className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-4">
              <Info size={14} /> Good to know
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
              {restaurantInfo.hours.map((h) => (
                <div key={h.label} className="flex justify-between text-[0.78rem] text-[var(--mm-cream-muted)]">
                  <span className="uppercase tracking-[0.12em]">{h.label.slice(0, 3)}</span>
                  <span className="tabular-nums">{h.time}</span>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 text-[0.78rem] text-[var(--mm-cream-muted)] leading-relaxed">
              <MapPin size={13} className="mt-0.5 flex-shrink-0 text-[var(--mm-accent)]" />
              <span>{restaurantInfo.address.line1}, {restaurantInfo.address.line2}</span>
            </div>
            <div className="flex items-start gap-2 text-[0.78rem] text-[var(--mm-cream-muted)] leading-relaxed mt-1.5">
              <Clock size={13} className="mt-0.5 flex-shrink-0 text-[var(--mm-accent)]" />
              <span>Tables are held for 15 minutes — leave your mobile and we&apos;ll text to confirm.</span>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
